// Chart 1 · Raw poll data, no combining
//
// Plain inline SVG, hand-rolled. Pollsters draw lines + dots over a
// background of party-territory zones, quarter gridlines, and labelled
// vertical milestone guides at the top. The EP 2024 result is anchored
// as a single gold dot.
//
// Two display modes:
//
//   compact     — homepage teaser. No interaction beyond hover-tooltip
//                 and a click-anywhere handler that the parent wires to
//                 expand the chart to fullscreen.
//   fullscreen  — full interactivity: click-pin tooltip, drag-to-pan,
//                 wheel-to-zoom centred on cursor, two-finger pinch,
//                 double-click to reset to full range.
//
// The X scale is dynamic: viewStart / viewEnd ISO dates drive the
// active window; quarter labels and milestones clip to that window.
// FULL_RANGE_START / FULL_RANGE_END define the absolute bounds beyond
// which neither pan nor zoom can travel.

import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ANCHOR, MILESTONES, POLLSTERS } from '../../../data/surveys';
import type { PollsterName } from '../../../data/surveys.types';
import { usePolls } from '../../../lib/usePolls';
import {
  separateSameDayPolls,
  type PollOnChart,
} from '../../../utils/combinedTrajectories';
import { PROJECTED_VOTERS, leadToVotes } from '../../../lib/votes';
import '../Surveys.css';

// ─── Layout constants ────────────────────────────────────────────────
const W = 1450;
const H = 480;
const PAD_L = 80;
const PAD_T = 60;
const PLOT_W = 1320;
const PLOT_H = 350;
const Y_MIN = -6;
const Y_MAX = 14;
const Y_STEP = 2;

const FULL_RANGE_START = '2024-06-01';
// Bumped from 2026-05-14 → 2026-07-01 on 2026-05-13 because a poll
// dated 2026-05-16 was being filtered off the chart's right edge
// (showed in the inspection table fine, just nowhere to render).
// Keeps a comfortable buffer past the election so we don't run
// into this every time a new pollster publishes.
const FULL_RANGE_END = '2026-07-01';
const MIN_TS = Date.parse(FULL_RANGE_START + 'T00:00:00Z');
const MAX_TS = Date.parse(FULL_RANGE_END + 'T00:00:00Z');
const MIN_SPAN_MS = 30 * 86_400_000; // can't zoom in past 30-day window
const MAX_SPAN_MS = MAX_TS - MIN_TS;
const MS_PER_DAY = 86_400_000;

// ─── Axis labels ─────────────────────────────────────────────────────
const ALL_QUARTERS: { date: string; label: string }[] = [
  { date: '2024-09-01', label: 'Sep 2024' },
  { date: '2025-01-01', label: 'Jan 2025' },
  { date: '2025-04-01', label: 'Apr 2025' },
  { date: '2025-07-01', label: 'Jul 2025' },
  { date: '2025-10-01', label: 'Oct 2025' },
  { date: '2026-01-01', label: 'Jan 2026' },
  { date: '2026-04-01', label: 'Apr 2026' },
];

// Drawing order — Sagalytics last so its line sits on top.
const POLLSTER_DRAW_ORDER: PollsterName[] = [
  'MaltaToday',
  'Esprimi (Times of Malta)',
  'Sagalytics',
];

// ─── Helpers ─────────────────────────────────────────────────────────
const formatYTick = (v: number) => (v > 0 ? `+${v}` : `${v}`);

// Milestone bar geometry — single horizontal strip above the plot.
const MILESTONE_BAR_TOP = 14;
const MILESTONE_BAR_HEIGHT = 26;
const MILESTONE_BAR_BOT = MILESTONE_BAR_TOP + MILESTONE_BAR_HEIGHT;
const MILESTONE_LABEL_Y = MILESTONE_BAR_TOP + 18;

function tsToIso(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

// ─── Component ───────────────────────────────────────────────────────

interface SelectedPoll extends PollOnChart {
  pollster: PollsterName;
}

export type ChartMode = 'compact' | 'fullscreen';

export interface RawChartProps {
  /** compact: homepage teaser, no zoom, click bubbles up.
   *  fullscreen: full gesture-based zoom + click-pin tooltips. */
  mode?: ChartMode;
}

interface View {
  startTs: number;
  endTs: number;
}

const FULL_VIEW: View = { startTs: MIN_TS, endTs: MAX_TS };

export default function RawChart({ mode = 'compact' }: RawChartProps = {}) {
  const [view, setView] = useState<View>(FULL_VIEW);

  const chartStart = tsToIso(view.startTs);
  const chartEnd = tsToIso(view.endTs);

  const X = (date: string) => {
    const ts = Date.parse(date + 'T00:00:00Z');
    return PAD_L + ((ts - view.startTs) / (view.endTs - view.startTs)) * PLOT_W;
  };
  const Y = (lead: number) =>
    PAD_T + ((Y_MAX - lead) / (Y_MAX - Y_MIN)) * PLOT_H;

  const yZero = Y(0);
  const yTop = Y(Y_MAX);
  const yBot = Y(Y_MIN);
  const yMid = (yTop + yBot) / 2;

  // Hover (desktop) and pinned (touch / click) state.
  const [hovered, setHovered] = useState<SelectedPoll | null>(null);
  const [pinned, setPinned] = useState<SelectedPoll | null>(null);
  const selected = pinned ?? hovered;

  const { polls: allPolls } = usePolls();

  const polls = useMemo(
    () =>
      separateSameDayPolls(
        allPolls.filter(
          (p) => p.pollster !== '2024 EP Election (actual result)',
        ),
      ),
    [allPolls],
  );

  const visiblePolls = useMemo(
    () =>
      polls.filter(
        (p) => p.chart_date >= chartStart && p.chart_date <= chartEnd,
      ),
    [polls, chartStart, chartEnd],
  );

  const byPollster = new Map<PollsterName, PollOnChart[]>();
  for (const p of visiblePolls) {
    const list = byPollster.get(p.pollster) ?? [];
    list.push(p);
    byPollster.set(p.pollster, list);
  }

  // Quarter ticks: pick from ALL_QUARTERS if any fall in window, else
  // synthesize evenly-spaced ticks at month granularity so the axis
  // never goes label-less when zoomed in.
  const visibleQuarters = useMemo<{ date: string; label: string }[]>(() => {
    const inWindow = ALL_QUARTERS.filter(
      (q) => q.date >= chartStart && q.date <= chartEnd,
    );
    if (inWindow.length >= 2) return inWindow;
    return synthesizeTicks(view.startTs, view.endTs);
  }, [chartStart, chartEnd, view.startTs, view.endTs]);
  const visibleMilestones = MILESTONES.filter(
    (m) => m.date >= chartStart && m.date <= chartEnd,
  );
  const showAnchor = ANCHOR.date >= chartStart && ANCHOR.date <= chartEnd;

  // ─── Gesture handlers (fullscreen only) ────────────────────────────
  // Drag = rubber-band selection, release zooms to that range.
  // Wheel = zoom centred on cursor.
  // Pinch (2 fingers) = zoom centred between fingers.
  // Double-click = reset.
  // Bare click (no significant movement) on background = dismiss tooltip.
  const svgRef = useRef<SVGSVGElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const pointersRef = useRef(new Map<number, { clientX: number }>());
  const pinchRef = useRef<{ lastDist: number } | null>(null);
  // Live rubber-band rectangle; null when not actively selecting.
  const [selection, setSelection] = useState<{
    startSvgX: number;
    currentSvgX: number;
  } | null>(null);
  // Set when the latest gesture passes the "click vs drag" threshold,
  // so the synthetic click that follows a drag doesn't dismiss a
  // pinned tooltip.
  const didDragRef = useRef(false);

  function clientXToSvgX(clientX: number): number {
    const svg = svgRef.current;
    if (!svg) return PAD_L;
    const rect = svg.getBoundingClientRect();
    return ((clientX - rect.left) / rect.width) * W;
  }

  function zoomAround(svgX: number, factor: number) {
    setView(({ startTs, endTs }) => {
      const span = endTs - startTs;
      const newSpan = clamp(span * factor, MIN_SPAN_MS, MAX_SPAN_MS);
      if (newSpan === span) return { startTs, endTs };
      const cursorFrac = clamp((svgX - PAD_L) / PLOT_W, 0, 1);
      const tsAtCursor = startTs + cursorFrac * span;
      let newStart = tsAtCursor - cursorFrac * newSpan;
      let newEnd = newStart + newSpan;
      if (newStart < MIN_TS) {
        newEnd += MIN_TS - newStart;
        newStart = MIN_TS;
      }
      if (newEnd > MAX_TS) {
        newStart -= newEnd - MAX_TS;
        newEnd = MAX_TS;
      }
      newStart = Math.max(MIN_TS, newStart);
      newEnd = Math.min(MAX_TS, newEnd);
      return { startTs: newStart, endTs: newEnd };
    });
  }

  function zoomToSvgRange(x1: number, x2: number) {
    const lo = Math.max(PAD_L, Math.min(x1, x2));
    const hi = Math.min(PAD_L + PLOT_W, Math.max(x1, x2));
    if (hi - lo < 8) return;
    const loFrac = (lo - PAD_L) / PLOT_W;
    const hiFrac = (hi - PAD_L) / PLOT_W;
    setView(({ startTs, endTs }) => {
      const span = endTs - startTs;
      const newStart = startTs + loFrac * span;
      const newEnd = startTs + hiFrac * span;
      if (newEnd - newStart < MIN_SPAN_MS) return { startTs, endTs };
      return { startTs: newStart, endTs: newEnd };
    });
  }

  // Compact mobile: scroll to the right edge on mount so the latest
  // poll is in view first. The scroller has overflow-x:auto and the
  // CSS gives the SVG a min-width on phones.
  const scrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mode !== 'compact') return;
    const el = scrollerRef.current;
    if (!el) return;
    const apply = () => {
      el.scrollLeft = el.scrollWidth - el.clientWidth;
    };
    apply();
    // Also after the SVG loads — its rendered width can change once
    // images / fonts settle.
    const t = window.setTimeout(apply, 50);
    return () => window.clearTimeout(t);
  }, [mode]);

  // Wheel — manually attached to bypass React's passive listener.
  useEffect(() => {
    if (mode !== 'fullscreen') return;
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY > 0 ? 1.18 : 1 / 1.18;
      zoomAround(clientXToSvgX(e.clientX), factor);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [mode]);

  function onPointerDown(e: React.PointerEvent<SVGSVGElement>) {
    if (mode !== 'fullscreen') return;
    e.currentTarget.setPointerCapture(e.pointerId);
    pointersRef.current.set(e.pointerId, { clientX: e.clientX });
    if (pointersRef.current.size === 1) {
      const x = clientXToSvgX(e.clientX);
      setSelection({ startSvgX: x, currentSvgX: x });
      didDragRef.current = false;
    } else if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = { lastDist: Math.abs(a.clientX - b.clientX) };
      // A second finger lands → cancel the rubber-band, switch to pinch
      setSelection(null);
    }
  }

  function onPointerMove(e: React.PointerEvent<SVGSVGElement>) {
    if (mode !== 'fullscreen') return;
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { clientX: e.clientX });

    if (pointersRef.current.size === 1) {
      setSelection((s) => {
        if (!s) return null;
        const next = { ...s, currentSvgX: clientXToSvgX(e.clientX) };
        if (Math.abs(next.currentSvgX - next.startSvgX) > 4) {
          didDragRef.current = true;
        }
        return next;
      });
    } else if (pointersRef.current.size === 2 && pinchRef.current) {
      const [a, b] = [...pointersRef.current.values()];
      const dist = Math.abs(a.clientX - b.clientX);
      if (dist > 0 && pinchRef.current.lastDist > 0) {
        const factor = pinchRef.current.lastDist / dist;
        const midClientX = (a.clientX + b.clientX) / 2;
        zoomAround(clientXToSvgX(midClientX), factor);
        didDragRef.current = true;
      }
      pinchRef.current.lastDist = dist;
    }
  }

  function onPointerUp(e: React.PointerEvent<SVGSVGElement>) {
    if (mode !== 'fullscreen') return;
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) {
      // Commit a non-trivial rubber-band selection as a zoom.
      if (selection) {
        const dx = Math.abs(selection.currentSvgX - selection.startSvgX);
        if (dx > 8) {
          zoomToSvgRange(selection.startSvgX, selection.currentSvgX);
        }
        setSelection(null);
      }
    }
  }

  function onDoubleClick() {
    if (mode !== 'fullscreen') return;
    setView(FULL_VIEW);
    setSelection(null);
  }

  const isFullscreen = mode === 'fullscreen';

  return (
    <div
      ref={wrapRef}
      className={`raw-chart-wrap${isFullscreen ? ' raw-chart-wrap--fullscreen' : ' raw-chart-wrap--compact'}`}
    >
      <div
        ref={scrollerRef}
        className="raw-chart-scroller"
        // Tapping bare chart area dismisses the pinned tooltip in
        // fullscreen — but only for *bare* taps. After a pan/pinch the
        // browser fires a synthetic click which we ignore here so it
        // doesn't dismiss the user's pinned tooltip mid-gesture.
        onClick={() => {
          if (!isFullscreen) return;
          if (didDragRef.current) {
            didDragRef.current = false;
            return;
          }
          setPinned(null);
        }}
      >
        <svg
          ref={svgRef}
          className="raw-chart"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Raw poll readings per pollster"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
          style={isFullscreen ? { touchAction: 'none' } : undefined}
        >
          {/* Background party-territory zones */}
          <rect
            className="chart-zone-pl"
            x={PAD_L}
            y={yTop}
            width={PLOT_W}
            height={yZero - yTop}
          />
          <rect
            className="chart-zone-pn"
            x={PAD_L}
            y={yZero}
            width={PLOT_W}
            height={yBot - yZero}
          />
          <text
            className="chart-zone-label chart-zone-label--pl"
            x={PAD_L + 12}
            y={yTop + 22}
          >
            PL ahead
          </text>
          <text
            className="chart-zone-label chart-zone-label--pn"
            x={PAD_L + 12}
            y={yBot - 12}
          >
            PN ahead
          </text>

          {/* Y-axis gridlines + labels */}
          {(() => {
            const out = [];
            for (let v = Y_MIN; v <= Y_MAX; v += Y_STEP) {
              const yy = Y(v);
              out.push(
                <g key={`yg-${v}`}>
                  <line
                    className="chart-grid"
                    x1={PAD_L}
                    y1={yy}
                    x2={PAD_L + PLOT_W}
                    y2={yy}
                  />
                  <text
                    className="chart-label"
                    x={PAD_L - 10}
                    y={yy + 5}
                    textAnchor="end"
                  >
                    {formatYTick(v)}
                  </text>
                </g>,
              );
            }
            return out;
          })()}

          {/* Dashed zero line */}
          <line
            className="chart-zero"
            x1={PAD_L}
            y1={yZero}
            x2={PAD_L + PLOT_W}
            y2={yZero}
          />

          {/* Y-axis title */}
          <text
            className="chart-axis-title"
            x={20}
            y={yMid}
            transform={`rotate(-90 20,${yMid})`}
            textAnchor="middle"
          >
            PL lead (pp)
          </text>

          {/* Quarter / synthesized x-axis lines + labels */}
          {visibleQuarters.map((q) => {
            const xx = X(q.date);
            if (xx < PAD_L || xx > PAD_L + PLOT_W) return null;
            return (
              <g key={`${q.date}-${q.label}`}>
                <line
                  className="chart-grid"
                  x1={xx}
                  y1={PAD_T}
                  x2={xx}
                  y2={yBot}
                />
                <text
                  className="chart-label"
                  x={xx}
                  y={yBot + 22}
                  textAnchor="middle"
                >
                  {q.label}
                </text>
              </g>
            );
          })}

          {/* Milestone bar — one continuous horizontal strip at the
              top of the chart, with every visible label on the same
              line. Tick marks inside the bar mark each milestone's X
              and a dashed line drops from the tick to the chart
              bottom. The bar moves with the chart on zoom because
              X(m.date) is recomputed from the active view. */}
          {visibleMilestones.length > 0 && (
            <>
              <rect
                className="chart-milestone-bar"
                x={PAD_L}
                y={MILESTONE_BAR_TOP}
                width={PLOT_W}
                height={MILESTONE_BAR_HEIGHT}
                rx={3}
              />
              {visibleMilestones.map((m) => {
                const xx = X(m.date);
                if (xx < PAD_L || xx > PAD_L + PLOT_W) return null;
                return (
                  <g key={m.label}>
                    <line
                      className="chart-milestone"
                      x1={xx}
                      y1={MILESTONE_BAR_BOT}
                      x2={xx}
                      y2={yBot}
                    />
                    <line
                      className="chart-milestone-tick"
                      x1={xx}
                      y1={MILESTONE_BAR_TOP}
                      x2={xx}
                      y2={MILESTONE_BAR_BOT}
                    />
                    <text
                      className="chart-milestone-label"
                      x={xx}
                      y={MILESTONE_LABEL_Y}
                      textAnchor="middle"
                    >
                      {m.label}
                    </text>
                  </g>
                );
              })}
            </>
          )}

          {/* Pollster lines + dots — each line connects back to the EP
              2024 anchor so the reader can follow each pollster's drift
              from the most-recent ground-truth result forward. The
              anchor connection only renders when the anchor is in view. */}
          {POLLSTER_DRAW_ORDER.map((name) => {
            const arr = byPollster.get(name) ?? [];
            if (arr.length === 0) return null;
            const color = POLLSTERS[name].color;
            const linePts: { chart_date: string; lead: number }[] = showAnchor
              ? [{ chart_date: ANCHOR.date, lead: ANCHOR.lead }, ...arr]
              : arr;
            const path = linePts
              .map(
                (p, i) =>
                  `${i === 0 ? 'M' : 'L'} ${X(p.chart_date)},${Y(p.lead)}`,
              )
              .join(' ');
            return (
              <g key={name}>
                <path className="chart-poll-line" d={path} stroke={color} />
                {arr.map((p) => {
                  const cx = X(p.chart_date);
                  const cy = Y(p.lead);
                  const isSelected =
                    selected !== null &&
                    selected.pollster === name &&
                    selected.chart_date === p.chart_date;
                  return (
                    <g key={`${name}-${p.chart_date}`}>
                      <circle
                        className="chart-poll-dot"
                        cx={cx}
                        cy={cy}
                        r={isSelected ? 8.5 : 6}
                        fill={color}
                        pointerEvents="none"
                      />
                      <circle
                        className="chart-poll-hit"
                        cx={cx}
                        cy={cy}
                        r={20}
                        fill="transparent"
                        onMouseEnter={() => setHovered({ ...p, pollster: name })}
                        onMouseLeave={() => setHovered(null)}
                        onPointerDown={(e) => {
                          // In fullscreen, prevent the SVG-level pan
                          // gesture from starting on a dot tap.
                          if (isFullscreen) e.stopPropagation();
                        }}
                        onClick={(e) => {
                          // Tap pins the readout — works the same in
                          // compact and fullscreen. (No more bubbling
                          // out to a wrapping expand button.)
                          e.stopPropagation();
                          setPinned((prev) =>
                            prev &&
                            prev.pollster === name &&
                            prev.chart_date === p.chart_date
                              ? null
                              : { ...p, pollster: name },
                          );
                        }}
                      />
                    </g>
                  );
                })}
              </g>
            );
          })}

          {/* EP 2024 anchor */}
          {showAnchor && (
            <circle
              className="chart-anchor"
              cx={X(ANCHOR.date)}
              cy={Y(ANCHOR.lead)}
              r={8}
            />
          )}

          {/* Live rubber-band selection rectangle while the user drags
              to choose a zoom range. */}
          {isFullscreen &&
            selection &&
            Math.abs(selection.currentSvgX - selection.startSvgX) > 4 && (
              <rect
                className="chart-selection"
                x={Math.min(selection.startSvgX, selection.currentSvgX)}
                y={PAD_T}
                width={Math.abs(selection.currentSvgX - selection.startSvgX)}
                height={PLOT_H}
                pointerEvents="none"
              />
            )}
        </svg>

      </div>

      {/* Tooltip is portaled to <body> so it can never be clipped by
          an overflow ancestor and can flip above/below based on real
          viewport space. Its position is recomputed each frame from
          the SVG's screen CTM so it tracks scrolls and pans. */}
      {selected && (
        <PollTooltip
          poll={selected}
          isPinned={pinned !== null}
          svgX={X(selected.chart_date)}
          svgY={Y(selected.lead)}
          svgRef={svgRef}
          onDismiss={() => {
            setPinned(null);
            setHovered(null);
          }}
        />
      )}

      {isFullscreen &&
        (view.startTs !== MIN_TS || view.endTs !== MAX_TS) && (
          <button
            type="button"
            className="raw-chart__reset"
            onClick={() => setView(FULL_VIEW)}
            title="Reset to full range"
            aria-label="Reset to full range"
          >
            <span aria-hidden="true">↺</span>
          </button>
        )}
    </div>
  );
}

// ─── Tooltip ─────────────────────────────────────────────────────────

// Estimated tooltip dimensions for the position-clamp math. Real
// dimensions are read after the first paint via ref so the second
// frame's clamp is exact; first frame uses these as a hint.
const TIP_W_EST = 240;
const TIP_H_EST = 220;
const TIP_GAP = 12;

function PollTooltip({
  poll,
  isPinned,
  svgX,
  svgY,
  svgRef,
  onDismiss,
}: {
  poll: SelectedPoll;
  isPinned: boolean;
  svgX: number;
  svgY: number;
  svgRef: React.RefObject<SVGSVGElement>;
  onDismiss: () => void;
}) {
  const tipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number; above: boolean } | null>(
    null,
  );

  // Compute viewport-pixel position from SVG coords using getScreenCTM
  // so the tooltip tracks the dot through any ancestor transforms /
  // scrolls / responsive scaling. Re-run on scroll + resize so the
  // tooltip stays glued to the dot.
  useLayoutEffect(() => {
    function update() {
      const svg = svgRef.current;
      if (!svg) return;
      const ctm = svg.getScreenCTM();
      if (!ctm) return;
      const point = svg.createSVGPoint();
      point.x = svgX;
      point.y = svgY;
      const dot = point.matrixTransform(ctm);

      const tipW = tipRef.current?.offsetWidth ?? TIP_W_EST;
      const tipH = tipRef.current?.offsetHeight ?? TIP_H_EST;
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      const spaceAbove = dot.y;
      const spaceBelow = vh - dot.y;
      // Prefer below; flip up only when there's clearly more room above.
      const above =
        spaceBelow < tipH + TIP_GAP + 8 && spaceAbove > tipH + TIP_GAP + 8;
      const y = above ? dot.y - TIP_GAP - tipH : dot.y + TIP_GAP;

      // Clamp X so the tooltip never sits half-off the screen.
      const half = tipW / 2;
      const x = Math.max(8 + half, Math.min(dot.x, vw - half - 8));

      setPos({ x, y, above });
    }
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [svgX, svgY, svgRef]);

  const meta = POLLSTERS[poll.pollster];
  const sign = poll.lead > 0 ? '+' : '';
  const winnerLabel =
    poll.lead > 0 ? 'PL ahead' : poll.lead < 0 ? 'PN ahead' : 'Tied';
  const votes = leadToVotes(poll.lead);

  if (!pos) {
    // Render once invisibly so we can measure the real size, then
    // re-render with the correct clamped position.
    return createPortal(
      <div
        ref={tipRef}
        className="chart-tooltip chart-tooltip--portal"
        style={{ left: -9999, top: -9999, visibility: 'hidden' }}
        aria-hidden="true"
      />,
      document.body,
    );
  }

  return createPortal(
    <div
      ref={tipRef}
      className={`chart-tooltip chart-tooltip--portal${
        pos.above ? ' chart-tooltip--above' : ' chart-tooltip--below'
      }${isPinned ? ' chart-tooltip--pinned' : ''}`}
      style={{ left: pos.x, top: pos.y }}
      role="dialog"
      aria-live="polite"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="chart-tooltip__header">
        <span
          className="chart-tooltip__pill"
          style={{ background: meta.abbr_bg }}
        >
          {meta.abbr}
        </span>
        <span className="chart-tooltip__pollster">{meta.fullname}</span>
        {isPinned && (
          <button
            type="button"
            className="chart-tooltip__close"
            onClick={onDismiss}
            aria-label="Close tooltip"
          >
            ×
          </button>
        )}
      </div>
      <div className="chart-tooltip__body">
        <div className="chart-tooltip__lead">
          <strong>
            {sign}
            {poll.lead.toFixed(1)}pp
          </strong>{' '}
          <span className="chart-tooltip__winner">{winnerLabel}</span>
        </div>
        <div className="chart-tooltip__votes">
          ≈ {votes.toLocaleString('en-GB')} votes of ~
          {PROJECTED_VOTERS.toLocaleString('en-GB')} at 85% turnout
        </div>
        <dl className="chart-tooltip__shares">
          {poll.pl != null && (
            <>
              <dt>PL</dt>
              <dd>{poll.pl.toFixed(1)}%</dd>
            </>
          )}
          {poll.pn != null && (
            <>
              <dt>PN</dt>
              <dd>{poll.pn.toFixed(1)}%</dd>
            </>
          )}
          {poll.adpd != null && poll.adpd > 0 && (
            <>
              <dt>ADPD</dt>
              <dd>{poll.adpd.toFixed(1)}%</dd>
            </>
          )}
          {poll.mom != null && poll.mom > 0 && (
            <>
              <dt>Mom</dt>
              <dd>{poll.mom.toFixed(1)}%</dd>
            </>
          )}
          {poll.none != null && poll.none > 0 && (
            <>
              <dt>None</dt>
              <dd>{poll.none.toFixed(1)}%</dd>
            </>
          )}
          {poll.sample != null && (
            <>
              <dt>Sample</dt>
              <dd>{poll.sample.toLocaleString('en-GB')}</dd>
            </>
          )}
        </dl>
        <div className="chart-tooltip__date">
          {poll.field || formatChartDate(poll.date)}
        </div>
        {poll.url && (
          <a
            className="chart-tooltip__source"
            href={poll.url}
            target="_blank"
            rel="noreferrer noopener"
          >
            Source ↗
          </a>
        )}
      </div>
    </div>,
    document.body,
  );
}

function formatChartDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Tick synthesis (used when zoom is tighter than ALL_QUARTERS) ───

function synthesizeTicks(
  startTs: number,
  endTs: number,
): { date: string; label: string }[] {
  const spanDays = (endTs - startTs) / MS_PER_DAY;
  // Aim for 4-6 ticks. Pick a stride that yields roughly that.
  const targetTicks = 5;
  const strideDays = Math.max(7, Math.round(spanDays / targetTicks));
  const out: { date: string; label: string }[] = [];
  let ts = startTs;
  // Snap to the start of the next week.
  const startOfNext = new Date(ts);
  startOfNext.setUTCDate(startOfNext.getUTCDate() + 1);
  ts = startOfNext.getTime();
  while (ts <= endTs && out.length < 8) {
    const d = new Date(ts);
    out.push({
      date: d.toISOString().slice(0, 10),
      label: d.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      }),
    });
    ts += strideDays * MS_PER_DAY;
  }
  return out;
}
